import { apiDelete, apiGet, apiPatch, apiPost } from "./apiClient";
import { publishCustomerTypeChanged } from "../../../messaging/customerProfileEvents";

// The single most-reused lookup in the whole feature (6 call sites:
// QuerysetV4, ChatPanelHeader, NewQueryModal,
// CustomerProfileButton, Modals.js) — each hand-rolled the same
// success/data[0]/null unwrap independently. Centralized here.
export function fetchCustomerProfileByPhone(phone) {
  return apiGet(`/customers/get_full_profile?phone=${encodeURIComponent(phone)}`)
    .then(res => (res.data?.success && res.data.data?.length > 0) ? res.data.data[0] : null)
    .catch(() => null);
}

// For WATI hidden-number/username contacts, whatsappId arrives as "" — no
// phone to look up by. get_full_profile falls back to matching
// wati_contact_id/wati_bsuid when phone is absent; phone still takes
// precedence when present. Pass whichever of the three identifiers the
// chat actually has (typically phone alone, or contactId+bsuid alone).
export function fetchCustomerProfileByIdentity({ phone, contactId, bsuid }) {
  const params = new URLSearchParams();
  if (phone) params.set("phone", phone);
  if (contactId) params.set("contactId", contactId);
  if (bsuid) params.set("bsuid", bsuid);
  return apiGet(`/customers/get_full_profile?${params.toString()}`)
    .then(res => (res.data?.success && res.data.data?.length > 0) ? res.data.data[0] : null)
    .catch(() => null);
}

// Callers branch on res.data.success/res.data.message themselves (e.g. to
// throw a specific error), so the raw payload is returned as-is.
export function createCustomer(payload) {
  return apiPost("/whatsapp/customer_add", payload).then(res => res.data);
}

export function updateCustomer(payload) {
  return apiPost("/customers/update", payload).then(res => {
    const result = res.data;
    if (result?.success === true && Object.prototype.hasOwnProperty.call(payload, "type")) {
      publishCustomerTypeChanged({ customerId: payload.customer_id, customerType: payload.type ?? null });
    }
    return result;
  });
}

export function createCustomerAddress(customerId, payload) {
  return apiPost("/customers/" + customerId + "/addresses", payload).then(res => res.data);
}

export function updateCustomerAddress(customerId, addressId, payload) {
  return apiPatch("/customers/" + customerId + "/addresses/" + addressId, payload).then(res => res.data);
}

export function deleteCustomerAddress(customerId, addressId) {
  return apiDelete("/customers/" + customerId + "/addresses/" + addressId).then(res => res.data);
}


// WATI's own conversation search only matches its own stored contact name
// (or phone) — never contactId/bsuid/username. So when staff rename a
// customer in SRM (customer_real_name/customer_name), that name needs to be
// pushed back into WATI too, or the customer becomes unfindable by that name
// in the chat sidebar search, especially for hidden-number contacts who have
// no phone as a search fallback. WATI's contact-update API takes the WHOLE
// profile object, not a partial patch — fetch it, mutate the name fields,
// send the complete object back. Best-effort: callers should not block/undo
// the SRM save if this fails, just surface a warning.
export function fetchWatiContactProfile(customerId) {
  if (!Number.isSafeInteger(Number(customerId)) || Number(customerId) <= 0) return Promise.resolve(null);
  return apiGet(`/messaging-v1/customers/${encodeURIComponent(customerId)}/provider-profile`)
    .then(res => (res.data?.success ? res.data.data : null))
    .catch(() => null);
}

export function updateWatiContactProfile(customerId, postdata) {
  if (!Number.isSafeInteger(Number(customerId)) || Number(customerId) <= 0) return Promise.reject(new Error('A valid customer is required for WhatsApp profile sync.'));
  return apiPost(`/messaging-v1/customers/${encodeURIComponent(customerId)}/provider-profile`, { postdata }).then(res => res.data);
}

export function searchCustomers(search) {
  return apiPost("/whatsapp/get_customers", { search })
    .then(res => (res.data?.success ? (res.data.data || []) : []))
    .catch(() => []);
}

export function fetchCustomerList(filters = {}) {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.type != null) params.set("type", filters.type);
  if (filters.source != null) params.set("source", filters.source);
  if (filters.status != null) params.set("status", filters.status);
  if (filters.country) {
    const countryParam = Array.isArray(filters.country) ? filters.country.join(",") : filters.country;
    if (countryParam) params.set("country", countryParam);
  }
  params.set("page", filters.page ?? 1);
  params.set("page_size", filters.pageSize ?? 50);
  return apiGet(`/customers/get?${params.toString()}`)
    .then(res => res.data?.success
      ? { rows: res.data.data.rows, total: res.data.data.total, page: res.data.data.page, pageSize: res.data.data.page_size }
      : { rows: [], total: 0, page: 1, pageSize: 50 })
    .catch(() => ({ rows: [], total: 0, page: 1, pageSize: 50 }));
}

export function createCustomerRecord(payload) {
  return apiPost("/customers/create", payload).then(res => res.data);
}
